<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import integrationAPI from 'dashboard/api/integrations';

defineProps({
  selectedProduct: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'update:selectedProduct',
  'update:selectedVariantId',
]);

const { t } = useI18n();

const PRODUCT_PAGE_SIZE = 8;
const scrollLoadThreshold = 96;

const productQuery = ref('');
const productSearchError = ref('');
const productPaginationError = ref('');
const productResults = ref([]);
const productMeta = ref({});
const isSearchingProducts = ref(false);
const isLoadingMoreProducts = ref(false);
let productRequestSequence = 0;

const hasMoreProducts = computed(() => productMeta.value?.has_more === true);
const reachedEndOfProducts = computed(
  () => productResults.value.length > 0 && !hasMoreProducts.value
);

/**
 * Converts cached Shopify API products into the manual CRM event picker shape.
 *
 * @param {Object} product Cached Shopify product payload.
 * @returns {Object} Product picker row.
 */
function normalizeProduct(product) {
  const variants = Array.isArray(product.variants) ? product.variants : [];
  return {
    id: product.id,
    title: product.title,
    image: product.featured_image_url || variants[0]?.image_url || '',
    variants: variants.map(variant => {
      const selectedOptions = Array.isArray(variant.selected_options)
        ? variant.selected_options
        : [];
      const optionLabel = selectedOptions
        .map(option => [option.name, option.value].filter(Boolean).join(': '))
        .filter(Boolean)
        .join(' / ');

      return {
        id: variant.id,
        label: optionLabel || variant.title || variant.sku || product.title,
        sku: variant.sku,
      };
    }),
  };
}

/**
 * Fetches one Shopify product page while preserving the current search query.
 *
 * @param {number} page Page number to request.
 * @returns {Promise<Object>} Axios response data.
 */
function fetchProductPage(page) {
  return integrationAPI.searchShopifyProducts({
    query: productQuery.value.trim(),
    limit: PRODUCT_PAGE_SIZE,
    page,
  });
}

/**
 * Loads the first product page for the current search keyword.
 *
 * @returns {Promise<void>}
 */
async function runProductSearch() {
  productRequestSequence += 1;
  const sequence = productRequestSequence;
  isSearchingProducts.value = true;
  productSearchError.value = '';
  productPaginationError.value = '';

  try {
    const response = await fetchProductPage(1);
    if (sequence !== productRequestSequence) return;

    productResults.value = Array.isArray(response.data?.items)
      ? response.data.items.map(normalizeProduct)
      : [];
    productMeta.value = response.data?.meta || {};
  } catch {
    if (sequence !== productRequestSequence) return;

    productResults.value = [];
    productMeta.value = {};
    productSearchError.value = t('CRM_EVENT_CREATE.PRODUCT_SEARCH_ERROR');
  } finally {
    if (sequence === productRequestSequence) {
      isSearchingProducts.value = false;
    }
  }
}

/**
 * Appends the next Shopify product page for the current search keyword.
 *
 * @returns {Promise<void>}
 */
async function loadMoreProducts() {
  if (
    isSearchingProducts.value ||
    isLoadingMoreProducts.value ||
    !hasMoreProducts.value
  ) {
    return;
  }

  const sequence = productRequestSequence;
  const nextPage = (productMeta.value.current_page || 1) + 1;
  isLoadingMoreProducts.value = true;
  productPaginationError.value = '';

  try {
    const response = await fetchProductPage(nextPage);
    if (sequence !== productRequestSequence) return;

    const existingIds = new Set(
      productResults.value.map(product => product.id)
    );
    const nextProducts = Array.isArray(response.data?.items)
      ? response.data.items
          .map(normalizeProduct)
          .filter(product => !existingIds.has(product.id))
      : [];
    productResults.value = [...productResults.value, ...nextProducts];
    productMeta.value = response.data?.meta || {};
  } catch {
    if (sequence !== productRequestSequence) return;

    productPaginationError.value = t(
      'CRM_EVENT_CREATE.PRODUCT_LOAD_MORE_ERROR'
    );
  } finally {
    if (sequence === productRequestSequence) {
      isLoadingMoreProducts.value = false;
    }
  }
}

/**
 * Selects a trusted cached Shopify product for the manual event.
 *
 * @param {Object} product Product picker row.
 * @returns {void}
 */
function selectProduct(product) {
  emit('update:selectedProduct', product);
  emit('update:selectedVariantId', product.variants[0]?.id || '');
}

/**
 * Loads more products when the selector list reaches the bottom.
 *
 * @param {Event} event Scroll event from the product list container.
 * @returns {void}
 */
function handleProductListScroll(event) {
  const target = event.currentTarget;
  if (!target) return;

  const distanceToBottom =
    target.scrollHeight - target.scrollTop - target.clientHeight;
  if (distanceToBottom <= scrollLoadThreshold) {
    loadMoreProducts();
  }
}

watch(productQuery, runProductSearch);
onMounted(runProductSearch);
</script>

<template>
  <div class="grid gap-3">
    <label class="flex flex-col gap-1.5 text-sm">
      <span class="font-medium text-n-slate-12">
        {{ t('CRM_EVENT_CREATE.PRODUCT') }}
      </span>
      <input
        v-model="productQuery"
        data-testid="crm-product-search-input"
        type="search"
        class="h-10 rounded-md border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12"
        :placeholder="t('CRM_EVENT_CREATE.SEARCH_PRODUCT')"
      />
    </label>
    <p v-if="productSearchError" class="text-xs text-n-ruby-10">
      {{ productSearchError }}
    </p>
    <p v-else-if="isSearchingProducts" class="text-xs text-n-slate-10">
      {{ t('CRM_EVENT_CREATE.PRODUCT_SEARCHING') }}
    </p>
    <div
      v-else-if="productResults.length"
      data-testid="crm-product-results"
      class="max-h-[5.25rem] overflow-y-auto rounded-md border border-n-weak bg-n-solid-1"
      @scroll="handleProductListScroll"
    >
      <button
        v-for="product in productResults"
        :key="product.id"
        data-testid="shopify-product-result"
        type="button"
        class="flex min-h-14 w-full items-center gap-3 px-3 py-2 text-start hover:bg-n-alpha-2"
        :class="{
          'bg-n-alpha-2': selectedProduct?.id === product.id,
        }"
        @click="selectProduct(product)"
      >
        <img
          v-if="product.image"
          :src="product.image"
          :alt="product.title"
          class="size-10 shrink-0 rounded-md object-cover bg-n-alpha-2"
        />
        <span
          v-else
          class="size-10 shrink-0 rounded-md bg-n-alpha-2 text-n-slate-10 i-lucide-package"
        />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium text-n-slate-12">
            {{ product.title }}
          </span>
          <span
            v-if="product.variants[0]?.sku"
            class="block truncate text-xs text-n-slate-10"
          >
            {{ product.variants[0].sku }}
          </span>
        </span>
        <span
          v-if="selectedProduct?.id === product.id"
          class="i-lucide-check size-4 shrink-0 text-n-brand"
        />
      </button>
      <p
        v-if="isLoadingMoreProducts"
        data-testid="crm-products-loading-more"
        class="px-3 py-3 text-center text-xs text-n-slate-10"
      >
        {{ t('CRM_EVENT_CREATE.PRODUCT_LOADING_MORE') }}
      </p>
      <p
        v-else-if="productPaginationError"
        data-testid="crm-products-pagination-error"
        class="px-3 py-3 text-center text-xs text-n-ruby-10"
      >
        {{ productPaginationError }}
      </p>
      <p
        v-else-if="reachedEndOfProducts"
        data-testid="crm-products-end-of-list"
        class="px-3 py-3 text-center text-xs text-n-slate-10"
      >
        {{ t('CRM_EVENT_CREATE.PRODUCT_END_OF_LIST') }}
      </p>
    </div>
    <p v-else class="text-xs text-n-slate-10">
      {{ t('CRM_EVENT_CREATE.PRODUCT_PLACEHOLDER_HELP') }}
    </p>
  </div>
</template>
